import {
  generateMissingWordCandidates,
  generateIncorrectWordCandidates,
  generateWordOrderCandidates,
  estimateMissingWordsSpace,
  estimateWordOrderSpace,
  FEASIBLE_SECONDS_THRESHOLD,
  formatDuration,
  type MnemonicCandidate,
  type SearchSpaceEstimate,
} from "@app/bip39-core";
import { seedFromMnemonic, deriveAddress } from "./derive";
import { STANDARD_PATH_TEMPLATES, buildPath, type PathRangeOptions } from "./paths";
import type { ScriptType } from "./paths";

/**
 * A mutable cancellation flag. Deliberately not a real AbortController/
 * AbortSignal — those don't reliably structured-clone across a
 * postMessage boundary in every environment, and all we need here is a
 * flag one side can set and the other can poll. The caller creates one
 * object and can flip `.aborted = true` from outside at any time.
 */
export interface CancellationToken {
  aborted: boolean;
}

export function createCancellationToken(): CancellationToken {
  return { aborted: false };
}

export type ProgressCallback = (checked: number, estimatedTotal: number) => void;

export interface RecoveryOptions {
  passphrase?: string;
  targetAddress?: string; // public address to verify against, if the user has it
  /** Safety valve: stop after checking this many checksum-valid candidates against the target address. */
  maxAddressChecks?: number;
  pathOptions?: PathRangeOptions;
  signal?: CancellationToken;
  onProgress?: ProgressCallback;
  /**
   * Per-candidate address-verification cost in seconds, if the caller has
   * already measured it (the UI does, for its estimate) — lets the
   * feasibility gate use the same number the user was shown. Measured via
   * calibrateAddressCheckSeconds() when omitted and an address is supplied.
   */
  addressCheckSeconds?: number;
}

export interface RecoveryOutcome {
  /** Every checksum-valid candidate found (bounded by search-space feasibility checks upstream). */
  candidates: MnemonicCandidate[];
  /** The single verified match, if the target address was supplied and found. */
  verifiedMatch: MnemonicCandidate | null;
  truncated: boolean;
  /** True if a cancellation token was set mid-search — results reflect a partial search. */
  cancelled: boolean;
}

// How long to run before yielding back to the event loop. This is what makes
// cancellation and progress reporting actually work — a tight synchronous
// loop can't be interrupted by a 'cancel' postMessage until it yields, no
// matter how often we check the flag inside it. Time-based rather than
// count-based: per-candidate cost ranges from ~microseconds (a failed
// checksum) to ~100ms (a checksum-valid candidate verified against an
// address), so any fixed count is either wasteful or unresponsive.
const YIELD_INTERVAL_MS = 50;
const DEFAULT_MAX_ADDRESS_CHECKS = 5_000;
// Reading the clock on every candidate is measurable overhead on the fast
// (checksum-only) path, so only check it every N candidates.
const CLOCK_CHECK_EVERY = 64;

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function yieldToEventLoop(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

function checkCandidateAgainstAddress(
  mnemonic: string,
  passphrase: string,
  targetAddress: string,
  pathOptions: PathRangeOptions
): { path: string; scriptType: ScriptType; address: string } | null {
  const seed = seedFromMnemonic(mnemonic, passphrase);
  const maxAccount = pathOptions.maxAccount ?? 0;
  const maxIndex = pathOptions.maxIndex ?? 3; // check first few receive addresses by default
  for (const template of STANDARD_PATH_TEMPLATES) {
    for (let account = 0; account <= maxAccount; account++) {
      for (const change of [0, 1]) {
        for (let index = 0; index <= maxIndex; index++) {
          const path = buildPath(template.purpose, account, change, index);
          const derived = deriveAddress(seed, path, template.scriptType);
          if (derived.address === targetAddress.trim()) {
            return { path, scriptType: template.scriptType, address: derived.address };
          }
        }
      }
    }
  }
  return null;
}

/** Mnemonic used only to time the verification step — never a real secret. */
const CALIBRATION_MNEMONIC =
  "abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon abandon about";
/** A syntactically plausible address no path will derive, so calibration runs the full path loop. */
const CALIBRATION_NON_MATCHING_ADDRESS = "bc1qcalibrationaddressthatnevermatches0000";

const ADDRESS_CHECK_WARMUP_RUNS = 2;
const ADDRESS_CHECK_TIMED_RUNS = 2;
const addressCheckSecondsCache = new Map<string, number>();

/**
 * Measures how long verifying ONE checksum-valid candidate against a target
 * address takes on this device: seed derivation (PBKDF2) plus deriving and
 * encoding every standard path — the exact per-candidate work runRecovery
 * does. This dominates search time whenever an address is supplied (~0.2s
 * per candidate on a 1-vCPU Linux box under Node 22, vs. microseconds for a
 * checksum check). Two untimed warm-up runs first: measured there, runs
 * went ~0.7s, ~0.34s, then settled at ~0.21-0.25s — timing the second run
 * (one warm-up) still overestimated a real search by ~2x. Cached per path
 * range. Total calibration cost is ~4 checks (~1-2s on that slow machine).
 */
export function calibrateAddressCheckSeconds(pathOptions: PathRangeOptions = {}): number {
  const key = `${pathOptions.maxAccount ?? 0}:${pathOptions.maxIndex ?? 3}`;
  const cached = addressCheckSecondsCache.get(key);
  if (cached !== undefined) return cached;

  for (let i = 0; i < ADDRESS_CHECK_WARMUP_RUNS; i++) {
    checkCandidateAgainstAddress(CALIBRATION_MNEMONIC, "", CALIBRATION_NON_MATCHING_ADDRESS, pathOptions);
  }
  // Fastest of a few timed runs: interference (GC, other tabs/processes) can
  // only make a run slower, and a single sample swung the estimate by ~50%.
  let seconds = Infinity;
  for (let i = 0; i < ADDRESS_CHECK_TIMED_RUNS; i++) {
    const start = now();
    checkCandidateAgainstAddress(CALIBRATION_MNEMONIC, "", CALIBRATION_NON_MATCHING_ADDRESS, pathOptions);
    seconds = Math.min(seconds, (now() - start) / 1000);
  }
  addressCheckSecondsCache.set(key, seconds);
  return seconds;
}

/** Escape hatch for tests. */
export function resetAddressCheckCalibration(): void {
  addressCheckSecondsCache.clear();
}

export interface MissingWordsSearchEstimate extends SearchSpaceEstimate {
  /**
   * Expected number of checksum-valid candidates that will be verified
   * against the target address (0 when no address is supplied). An upper
   * bound on the work: the search stops early if it finds a match.
   */
  addressChecks: number;
}

/**
 * The time a missing-words search will actually take, including address
 * verification when a target address is supplied — not just the checksum
 * pass that estimateMissingWordsSpace covers. This is the feasibility gate
 * recoverMissingWords/recoverIncorrectWord enforce: with an address, the
 * combined time must fit the same threshold the checksum-only gate uses
 * (verification is usually the real bottleneck). Without one, it is exactly
 * the checksum-only estimate.
 *
 * Pass `addressCheckSeconds` if already measured (e.g. by a UI that
 * calibrated off the render path); otherwise it's measured here.
 */
export function estimateMissingWordsSearch(
  template: (string | null)[],
  options: {
    verifyAgainstAddress?: boolean;
    addressCheckSeconds?: number;
    maxAddressChecks?: number;
    pathOptions?: PathRangeOptions;
  } = {}
): MissingWordsSearchEstimate {
  const blanks = template.filter((w) => w === null).length;
  const base = estimateMissingWordsSpace(blanks);
  if (!options.verifyAgainstAddress) return { ...base, addressChecks: 0 };

  // A BIP-39 mnemonic of N words carries N/3 checksum bits, so on average
  // 1 in 2^(N/3) combinations is checksum-valid (1 in 16 for 12 words).
  // Other lengths are structurally invalid and never reach verification.
  const wordCount = template.length;
  const validFraction = [12, 15, 18, 21, 24].includes(wordCount) ? 2 ** -(wordCount / 3) : 0;
  const addressChecks = Math.min(
    Math.ceil(base.totalCandidates * validFraction),
    options.maxAddressChecks ?? DEFAULT_MAX_ADDRESS_CHECKS
  );
  const perCheck = options.addressCheckSeconds ?? calibrateAddressCheckSeconds(options.pathOptions);
  const estimatedSeconds = base.estimatedSeconds + addressChecks * perCheck;
  if (!base.feasible) {
    // Already over the limit on checksum time alone — that reason still stands.
    return { ...base, estimatedSeconds, addressChecks };
  }
  const feasible = estimatedSeconds <= FEASIBLE_SECONDS_THRESHOLD;
  return {
    ...base,
    estimatedSeconds,
    feasible,
    reason: feasible
      ? `${base.totalCandidates.toLocaleString()} candidates, roughly ${formatDuration(estimatedSeconds)} on ` +
        `this device including verifying ~${addressChecks.toLocaleString()} checksum-valid candidates against the address.`
      : `Verifying ~${addressChecks.toLocaleString()} checksum-valid candidates against your address would take ` +
        `roughly ${formatDuration(estimatedSeconds)} on this device (~${perCheck.toFixed(2)}s each). ` +
        `This is not run automatically.`,
    addressChecks,
  };
}

/** estimateMissingWordsSearch for one suspect word: the same search, with that position blanked. */
export function estimateIncorrectWordSearch(
  words: string[],
  suspectIndex: number,
  options: Parameters<typeof estimateMissingWordsSearch>[1] = {}
): MissingWordsSearchEstimate {
  const template = [...words] as (string | null)[];
  template[suspectIndex] = null;
  return estimateMissingWordsSearch(template, options);
}

function gateOptions(options: RecoveryOptions) {
  return {
    verifyAgainstAddress: Boolean(options.targetAddress),
    addressCheckSeconds: options.addressCheckSeconds,
    maxAddressChecks: options.maxAddressChecks,
    pathOptions: options.pathOptions,
  };
}

async function runRecovery(
  candidateGen: Generator<MnemonicCandidate>,
  options: RecoveryOptions,
  estimatedTotal: number
): Promise<RecoveryOutcome> {
  const passphrase = options.passphrase ?? "";
  const maxAddressChecks = options.maxAddressChecks ?? DEFAULT_MAX_ADDRESS_CHECKS;
  const pathOptions = options.pathOptions ?? {};

  const candidates: MnemonicCandidate[] = [];
  let verifiedMatch: MnemonicCandidate | null = null;
  // Every combination considered, valid or not — what progress reports
  // against estimatedTotal (which also counts every combination).
  let examined = 0;
  let addressChecks = 0;
  let truncated = false;
  let cancelled = false;
  let lastYield = now();

  for (const candidate of candidateGen) {
    examined++;
    const isCandidate = candidate.validity === "checksum-valid-unverified";
    if (isCandidate) candidates.push(candidate);

    if (isCandidate && options.targetAddress && !verifiedMatch) {
      if (addressChecks >= maxAddressChecks) {
        truncated = true;
      } else {
        addressChecks++;
        const match = checkCandidateAgainstAddress(
          candidate.mnemonic,
          passphrase,
          options.targetAddress,
          pathOptions
        );
        if (match) {
          verifiedMatch = {
            ...candidate,
            validity: "address-verified",
            matchedAddress: match.address,
            matchedPath: match.path,
          };
        }
      }
    }

    if (verifiedMatch) break; // found it — no reason to keep searching

    // Address checks are slow, so after one always look at the clock;
    // otherwise only every CLOCK_CHECK_EVERY candidates.
    const checkClock = (isCandidate && options.targetAddress) || examined % CLOCK_CHECK_EVERY === 0;
    if (checkClock && now() - lastYield >= YIELD_INTERVAL_MS) {
      options.onProgress?.(examined, estimatedTotal);
      await yieldToEventLoop();
      lastYield = now();
      if (options.signal?.aborted) {
        cancelled = true;
        break;
      }
    }
  }

  options.onProgress?.(examined, estimatedTotal);
  return { candidates, verifiedMatch, truncated, cancelled };
}

/** Scenario 1/2/3 share this shape: a template with blanks, or a full mnemonic with a suspect index/order. */
export async function recoverMissingWords(
  template: (string | null)[],
  options: RecoveryOptions = {}
): Promise<RecoveryOutcome> {
  const estimate = estimateMissingWordsSearch(template, gateOptions(options));
  if (!estimate.feasible) {
    throw new Error(estimate.reason);
  }
  return runRecovery(generateMissingWordCandidates(template, { includeInvalid: true }), options, estimate.totalCandidates);
}

export async function recoverIncorrectWord(
  words: string[],
  suspectIndex: number,
  options: RecoveryOptions = {}
): Promise<RecoveryOutcome> {
  const estimate = estimateIncorrectWordSearch(words, suspectIndex, gateOptions(options));
  if (!estimate.feasible) {
    throw new Error(estimate.reason);
  }
  return runRecovery(
    generateIncorrectWordCandidates(words, suspectIndex, { includeInvalid: true }),
    options,
    estimate.totalCandidates
  );
}

export async function recoverWordOrder(
  words: string[],
  options: RecoveryOptions = {}
): Promise<RecoveryOutcome> {
  const estimate = estimateWordOrderSpace(words.length);
  if (!estimate.feasible) {
    throw new Error(estimate.reason);
  }
  return runRecovery(generateWordOrderCandidates(words, undefined, { includeInvalid: true }), options, estimate.totalCandidates);
}
